// Condition Generation — Prompt, Parser, and Mapper

// Structured output prompt for generating V2 payment conditions via LLM.
// Used by the "Create with AI" flow in the Payment Conditions page.

import type {
	ComparisonOperator,
	DataRequestFieldType,
	ExpectationRule,
	VerificationSection,
} from '../@types';
import type { FormAddConditionValues } from '../components/forms/FormAddConditionWizard/FormAddConditionWizard';

// Types — AI Response Schema

export interface AiConditionField {
	label: string;
	fieldType: DataRequestFieldType;
	required: boolean;
	options?: string[];
	placeholder?: string;
	unit?: string;
	expectedResponse?: ExpectationRule | ExpectationRule[];
}

export interface AiConditionSection {
	name: string;
	fields: AiConditionField[];
}

export interface AiConditionResult {
	name: string;
	description: string;
	milestoneType: 'ADVANCE' | 'PROGRESS' | 'DELIVERABLE' | 'RETENTION' | 'COMPLIANCE' | 'CUSTOM';
	paymentPercentage: number | null;
	paymentAmount: number | null;
	sequenceOrder: number;
	verificationSections: AiConditionSection[];
	confidence: number;
	reasoning: string;
}

export interface GenerateConditionsResponse {
	isMilestoneRelevant: boolean;
	reason?: string;
	milestones: AiConditionResult[];
}

// System Prompt

export const CONDITION_GENERATION_PROMPT = `You are a World Bank disbursement specialist. Given a user's request (and optionally document context), generate payment milestone conditions for a contract.

Return ONLY valid JSON matching this schema exactly — no markdown, no commentary:

{
  "isMilestoneRelevant": true,
  "milestones": [
    {
      "name": "Advance Payment",
      "description": "Initial advance upon contract signing",
      "milestoneType": "ADVANCE",
      "paymentPercentage": 20,
      "paymentAmount": null,
      "sequenceOrder": 1,
      "verificationSections": [
        {
          "name": "Document Verification",
          "fields": [
            { "label": "Signed contract uploaded", "fieldType": "document", "required": true, "expectedResponse": { "operator": "exists" } },
            { "label": "Bank guarantee received", "fieldType": "yesno", "required": true, "expectedResponse": { "operator": "eq", "value": "yes" } },
            { "label": "Road completion length", "fieldType": "number", "required": true, "unit": "km" }
          ]
        }
      ],
      "confidence": 0.95,
      "reasoning": "Standard advance payment clause"
    }
  ]
}

If the request does NOT relate to payment milestones, return:
{ "isMilestoneRelevant": false, "reason": "...", "milestones": [] }

Rules:
- Valid fieldType values: "text", "number", "date", "select", "yesno", "document"
- Valid milestoneType values: "ADVANCE", "PROGRESS", "DELIVERABLE", "RETENTION", "COMPLIANCE", "CUSTOM"
- Payment percentages across ALL milestones must total <= 100%
- Use paymentPercentage (0-100) OR paymentAmount, not both
- Exclude milestones matching names in existingConditions
- Include at least one verificationSection per milestone
- Infer fieldType from context: amounts → number, dates → date, confirmations → yesno, file uploads → document, choices → select
- confidence is 0-1 indicating extraction certainty
- For "select" fields, include an "options" array
- For "number" fields, include a "unit" string when the unit is clear (e.g., "km", "meters", "USD", "%", "days")
- Include "expectedResponse" rules to define what answer is expected for each field:
  - yesno fields: typically { "operator": "eq", "value": "yes" }
  - document fields: { "operator": "exists" } (expect a document to be uploaded)
  - number fields: use "gte", "lte", "between", etc. when bounds are known (e.g., { "operator": "between", "value": 0, "secondaryValue": 100 })
  - date fields: use "lte" or "gte" when deadlines or start dates are known
  - text/select fields: use "eq", "contains", "in" when the expected answer is clear
  - Valid operators: "eq", "neq", "gt", "gte", "lt", "lte", "between", "contains", "not_contains", "starts_with", "ends_with", "in", "not_in", "matches", "exists", "not_exists"
  - For "between", include both "value" (lower bound) and "secondaryValue" (upper bound)
  - Omit expectedResponse when no meaningful expectation can be inferred

When documentInsights are provided:
- These are AI-extracted analyses from an uploaded document (contract, invoice, deliverable, etc.)
- Use disbursementConditions, keyInformationTable, and timelines to derive milestones with accurate payment percentages and amounts
- Use checklist items as verification fields
- Use keyRisks to add compliance-related verification sections
- Set higher confidence (0.8-0.95) when data comes directly from document extraction
- If the document insights clearly define a payment schedule, follow it exactly rather than inferring`;

// Response Parser

const VALID_FIELD_TYPES: DataRequestFieldType[] = [
	'text',
	'number',
	'date',
	'select',
	'yesno',
	'document',
];

const VALID_OPERATORS: ComparisonOperator[] = [
	'eq',
	'neq',
	'gt',
	'gte',
	'lt',
	'lte',
	'between',
	'contains',
	'not_contains',
	'starts_with',
	'ends_with',
	'in',
	'not_in',
	'matches',
	'exists',
	'not_exists',
];

/** Operators that don't require a value */
const VALUELESS_OPERATORS: ComparisonOperator[] = ['exists', 'not_exists'];

/** Which operators are valid per field type */
const OPERATORS_BY_FIELD_TYPE: Record<DataRequestFieldType, ComparisonOperator[]> = {
	yesno: ['eq', 'neq', 'in', 'not_in'],
	number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'],
	text: ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'ends_with', 'matches'],
	select: ['eq', 'neq', 'in', 'not_in'],
	date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'],
	document: ['exists', 'not_exists'],
};

/**
 * Validate a single expectation rule returned by the LLM.
 * Returns the validated rule or null if invalid.
 */
function validateExpectationRule(
	rule: Record<string, unknown>,
	fieldType: DataRequestFieldType
): ExpectationRule | null {
	const operator = rule.operator as ComparisonOperator;
	if (!VALID_OPERATORS.includes(operator)) return null;

	const allowedOps = OPERATORS_BY_FIELD_TYPE[fieldType];
	if (!allowedOps.includes(operator)) return null;

	if (VALUELESS_OPERATORS.includes(operator)) {
		return { operator };
	}

	if (rule.value === undefined || rule.value === null) return null;

	const result: ExpectationRule = { operator, value: rule.value as ExpectationRule['value'] };

	if (operator === 'between' && rule.secondaryValue != null) {
		result.secondaryValue = rule.secondaryValue as ExpectationRule['secondaryValue'];
	}

	return result;
}

/**
 * Parse expectedResponse from the LLM output: accepts a single rule object or an array.
 * Returns validated rules or undefined if nothing valid.
 */
function parseExpectedResponse(
	raw: unknown,
	fieldType: DataRequestFieldType
): ExpectationRule | ExpectationRule[] | undefined {
	if (!raw) return undefined;

	if (Array.isArray(raw)) {
		const validated = raw
			.map((r) => validateExpectationRule(r as Record<string, unknown>, fieldType))
			.filter((r): r is ExpectationRule => r !== null);
		if (validated.length === 0) return undefined;
		return validated.length === 1 ? validated[0] : validated;
	}

	if (typeof raw === 'object') {
		return validateExpectationRule(raw as Record<string, unknown>, fieldType) ?? undefined;
	}

	return undefined;
}

const VALID_MILESTONE_TYPES = [
	'ADVANCE',
	'PROGRESS',
	'DELIVERABLE',
	'RETENTION',
	'COMPLIANCE',
	'CUSTOM',
];

/**
 * Parse and validate the LLM's JSON response into a typed GenerateConditionsResponse.
 * Strips markdown fences if present and validates field types.
 */
export function parseAndValidateResponse(raw: string): GenerateConditionsResponse {
	// Strip markdown code fences if the LLM wraps the JSON
	let cleaned = raw.trim();
	if (cleaned.startsWith('```')) {
		cleaned = cleaned.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
	}

	const parsed = JSON.parse(cleaned);

	if (typeof parsed.isMilestoneRelevant !== 'boolean') {
		throw new Error('Response missing isMilestoneRelevant field');
	}

	if (!parsed.isMilestoneRelevant) {
		return {
			isMilestoneRelevant: false,
			reason: parsed.reason || 'No milestone data found',
			milestones: [],
		};
	}

	if (!Array.isArray(parsed.milestones)) {
		throw new Error('Response missing milestones array');
	}

	const milestones: AiConditionResult[] = parsed.milestones.map(
		(m: Record<string, unknown>, idx: number) => {
			// Validate milestone type
			const milestoneType = VALID_MILESTONE_TYPES.includes(m.milestoneType as string)
				? (m.milestoneType as AiConditionResult['milestoneType'])
				: 'CUSTOM';

			// Validate verification sections
			const sections: AiConditionSection[] = Array.isArray(m.verificationSections)
				? (m.verificationSections as Record<string, unknown>[]).map((s) => ({
						name: String(s.name || 'Verification'),
						fields: Array.isArray(s.fields)
							? (s.fields as Record<string, unknown>[]).map((f) => {
									const fieldType = VALID_FIELD_TYPES.includes(f.fieldType as DataRequestFieldType)
										? (f.fieldType as DataRequestFieldType)
										: 'text';
									const expectedResponse = parseExpectedResponse(f.expectedResponse, fieldType);
									return {
										label: String(f.label || 'Field'),
										fieldType,
										required: f.required !== false,
										...(f.options && Array.isArray(f.options)
											? { options: (f.options as unknown[]).map(String) }
											: {}),
										...(f.placeholder ? { placeholder: String(f.placeholder) } : {}),
										...(typeof f.unit === 'string' && f.unit.trim()
											? { unit: f.unit.trim().slice(0, 20) }
											: {}),
										...(expectedResponse ? { expectedResponse } : {}),
									};
								})
							: [],
					}))
				: [];

			return {
				name: String(m.name || `Milestone ${idx + 1}`),
				description: String(m.description || ''),
				milestoneType,
				paymentPercentage:
					typeof m.paymentPercentage === 'number'
						? Math.min(100, Math.max(0, m.paymentPercentage))
						: null,
				paymentAmount: typeof m.paymentAmount === 'number' ? m.paymentAmount : null,
				sequenceOrder: typeof m.sequenceOrder === 'number' ? m.sequenceOrder : idx + 1,
				verificationSections: sections,
				confidence: typeof m.confidence === 'number' ? Math.min(1, Math.max(0, m.confidence)) : 0.5,
				reasoning: String(m.reasoning || ''),
			};
		}
	);

	return { isMilestoneRelevant: true, milestones };
}

// Mapper — AI Result → FormAddConditionValues

/**
 * Transform an AI-generated condition into partial form values
 * ready to pre-fill FormAddConditionWizard.
 * Hierarchy fields (workPlanId, activityId, contractId) are left empty
 * for the user to select.
 */
export function mapAiResultToFormValues(
	result: AiConditionResult
): Partial<FormAddConditionValues> {
	const verificationSections: VerificationSection[] = result.verificationSections.map((s) => ({
		id: crypto.randomUUID(),
		name: s.name,
		assignedTo: null,
		assignedGroupId: null,
		fields: s.fields.map((f) => ({
			id: crypto.randomUUID(),
			label: f.label,
			fieldType: f.fieldType,
			required: f.required,
			...(f.options ? { options: f.options } : {}),
			...(f.placeholder ? { placeholder: f.placeholder } : {}),
			...(f.unit ? { unit: f.unit } : {}),
			...(f.expectedResponse ? { expectedResponse: f.expectedResponse } : {}),
		})),
		status: 'PENDING' as const,
		verifiedAt: null,
		verifiedBy: null,
	}));

	return {
		name: result.name,
		description: result.description,
		conditionType: 'contract',
		hasPaymentTerms: true,
		paymentType: result.paymentPercentage != null ? 'percentage' : 'fixed',
		paymentPercentage: result.paymentPercentage ?? '',
		paymentAmount: result.paymentAmount ?? '',
		sequenceOrder: result.sequenceOrder,
		verificationSections,
	};
}
